import { Injectable, inject } from '@angular/core';
import { TenantConfigService } from 'app/core/tenant/tenant-config.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { LocalStorageService } from '../local-storage.service';
import { ClarityService } from './clarity.service';

/** Persisted analytics-consent decision. `null` means the user has not chosen yet. */
export type ConsentDecision = 'granted' | 'denied' | null;

@Injectable({
    providedIn: 'root',
})
export class ConsentService {
    /** localStorage key holding the user's analytics consent decision. */
    private static readonly STORAGE_KEY = 'analyticsConsent';

    private readonly localStorage = inject(LocalStorageService);
    private readonly clarity = inject(ClarityService);
    private readonly tenantConfig = inject(TenantConfigService);

    /**
     * Master switch — Clarity is enabled solely by the tenant's backend
     * analytics flag (fail-closed when absent).
     */
    private get isClarityEnabled(): boolean {
        return (
            this.tenantConfig.safeGetConfig()?.analytics?.clarityEnabled ??
            false
        );
    }

    private readonly decision$ = new BehaviorSubject<ConsentDecision>(
        this.readStoredDecision()
    );

    /** Emits the current consent decision and future changes. */
    get consent$(): Observable<ConsentDecision> {
        return this.decision$.asObservable();
    }

    /** Current consent decision (synchronous). */
    get decision(): ConsentDecision {
        return this.decision$.value;
    }

    /** True when the user has already made a choice (banner should not show). */
    hasDecided(): boolean {
        return this.decision$.value !== null;
    }

    /**
     * Records the user's choice from the consent banner, persists it, and
     * applies it to Clarity (grant → init + consentV2 granted; deny →
     * consentV2 denied, no session recording).
     */
    setConsent(granted: boolean): void {
        const decision: ConsentDecision = granted ? 'granted' : 'denied';
        this.localStorage.set(ConsentService.STORAGE_KEY, decision);
        this.decision$.next(decision);
        this.applyToClarity(granted);
    }

    /**
     * Re-applies a previously stored decision on app start. Only acts when a
     * decision exists; a first-time visitor is left untouched until they choose.
     */
    applyStoredConsent(): void {
        const decision = this.decision$.value;
        if (decision === null) {
            return;
        }
        this.applyToClarity(decision === 'granted');
    }

    private applyToClarity(granted: boolean): void {
        // Respect the master analytics flag. When Clarity is disabled the
        // consent choice is still persisted, but nothing is sent to Clarity.
        if (!this.isClarityEnabled) {
            return;
        }
        if (granted) {
            void this.clarity.initializeClarity().then(() => {
                this.clarity.consentV2({
                    ad_Storage: 'denied',
                    analytics_Storage: 'granted',
                });
            });
        } else {
            this.clarity.consentV2({
                ad_Storage: 'denied',
                analytics_Storage: 'denied',
            });
        }
    }

    private readStoredDecision(): ConsentDecision {
        const stored = this.localStorage.get(ConsentService.STORAGE_KEY);
        return stored === 'granted' || stored === 'denied' ? stored : null;
    }
}
